import React, { useState, useEffect, useCallback } from 'react';
import { Card, Form, Button, Modal, Table } from 'react-bootstrap';
import StatusBulb from './StatusBulb';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import DateInputDMY from './DateInputDMY';
import { formatDateOnly, formatDateTime } from '../utils/formatDate';

// Monday-Sunday range containing `date`.
const weekRange = (date) => {
  const d = new Date(date);
  const day = (d.getDay() + 6) % 7; // 0 = Monday
  const monday = new Date(d);
  monday.setHours(0, 0, 0, 0);
  monday.setDate(d.getDate() - day);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  sunday.setHours(23, 59, 59, 999);
  return { start: monday, end: sunday };
};

const emptyForm = { component: '', lastDone: '', nextDueDate: '', notes: '' };

const PlantHealthPanel = () => {
  const [status, setStatus] = useState(null);
  const [weeklyHours, setWeeklyHours] = useState(0);
  const [items, setItems] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Stopping "operacion" (same stream this card's status/hours already
  // read) auto-cascades to stop O2/N2 server-side too — see POST
  // /api/production-events. Same reason-required flow cylinderAdder.js's
  // own stop button uses, just reachable from the admin dashboard too.
  const [showStopModal, setShowStopModal] = useState(false);
  const [stopReason, setStopReason] = useState('');
  const [stopNotes, setStopNotes] = useState('');
  const [stopSaving, setStopSaving] = useState(false);
  const [stopError, setStopError] = useState('');

  const fetchStatus = useCallback(async () => {
    try {
      const res = await fetch('/api/production-events/current');
      const data = await res.json();
      setStatus(data);
    } catch (err) {
      console.error('❌ Error fetching plant status:', err);
    }
  }, []);

  const fetchWeeklyHours = useCallback(async () => {
    try {
      const { start, end } = weekRange(new Date());
      const periods = new Set([
        `${start.getFullYear()}-${start.getMonth() + 1}`,
        `${end.getFullYear()}-${end.getMonth() + 1}`,
      ]);

      const dayHours = {};
      for (const period of periods) {
        const [year, month] = period.split('-');
        const res = await fetch(`/api/production-events/hours?month=${month}&year=${year}`);
        const data = await res.json();
        (data.calculatedHours || []).forEach((h) => { dayHours[h.date] = h.hours; });
      }

      let total = 0;
      Object.entries(dayHours).forEach(([date, hours]) => {
        const d = new Date(`${date}T00:00:00`);
        if (d >= start && d <= end) total += hours;
      });
      setWeeklyHours(total);
    } catch (err) {
      console.error('❌ Error fetching weekly production hours:', err);
    }
  }, []);

  const fetchItems = useCallback(async () => {
    try {
      const res = await fetch('/api/maintenance');
      const data = await res.json();
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('❌ Error fetching maintenance items:', err);
    }
  }, []);

  const openStopModal = () => {
    setStopReason('');
    setStopNotes('');
    setStopError('');
    setShowStopModal(true);
  };

  const handleStopProduction = async () => {
    if (!stopReason) {
      setStopError('Seleccione un motivo.');
      return;
    }

    setStopSaving(true);
    setStopError('');

    try {
      const res = await fetch('/api/production-events', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type: 'stop', stream: 'operacion', reason: stopReason, notes: stopNotes }),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error al detener la producción.');

      await fetchStatus();
      await fetchWeeklyHours();

      setShowStopModal(false);
      setStopReason('');
      setStopNotes('');
    } catch (err) {
      setStopError(err.message);
    } finally {
      setStopSaving(false);
    }
  };

  useEffect(() => {
    fetchStatus();
    fetchWeeklyHours();
    fetchItems();
  }, [fetchStatus, fetchWeeklyHours, fetchItems]);

  const openAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowModal(true);
  };

  const openEdit = (item) => {
    setEditingId(item._id);
    setForm({
      component: item.component,
      lastDone: item.lastDone ? item.lastDone.slice(0, 10) : '',
      nextDueDate: item.nextDueDate ? item.nextDueDate.slice(0, 10) : '',
      notes: item.notes || '',
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.component || !form.lastDone) return;
    try {
      const url = editingId ? `/api/maintenance/${editingId}` : '/api/maintenance';
      const method = editingId ? 'PUT' : 'POST';
      await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setShowModal(false);
      fetchItems();
    } catch (err) {
      console.error('❌ Error saving maintenance item:', err);
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    try {
      await fetch(`/api/maintenance/${itemToDelete._id}`, { method: 'DELETE' });
      setItemToDelete(null);
      fetchItems();
    } catch (err) {
      console.error('❌ Error deleting maintenance item:', err);
    }
  };

  return (
    <div className="row mb-4">
      <div className="col-md-4">
        <Card className="mb-4 h-100">
          <Card.Body>
            <Card.Title>Estado de la Planta</Card.Title>
            {status && (
              <>
                <div className="d-flex align-items-center mb-2" style={{ gap: '8px' }}>
                  <StatusBulb status={status.isRunning ? 'green' : 'red'} />
                  <strong>{status.isRunning ? 'En Producción' : 'Detenido'}</strong>
                </div>
                {!status.isRunning && (
                  <div style={{ fontSize: '0.9rem', color: 'var(--mm-muted)' }}>
                    {status.reason && <div>Motivo: {status.reason}</div>}
                    {status.notes && <div>Notas: {status.notes}</div>}
                    {status.since && <div>Desde: {formatDateTime(status.since)}</div>}
                  </div>
                )}
                {status.isRunning && (
                  <Button size="sm" variant="danger" className="mt-1" onClick={openStopModal}>
                    Detener Producción
                  </Button>
                )}
              </>
            )}
            <hr />
            <Card.Title style={{ fontSize: '1rem' }}>Horas en Producción Esta Semana</Card.Title>
            <div style={{ fontSize: '1.8rem', fontWeight: 600 }}>{weeklyHours.toFixed(1)} h</div>
          </Card.Body>
        </Card>
      </div>

      <div className="col-md-8">
        <Card className="mb-4 h-100">
          <Card.Body>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <Card.Title className="mb-0">Mantenimiento de Planta</Card.Title>
              <Button size="sm" onClick={openAdd}>Agregar</Button>
            </div>
            <div className="table-responsive">
              <Table size="sm" className="align-middle">
                <thead>
                  <tr>
                    <th>Componente</th>
                    <th>Última Vez</th>
                    <th>Próximo Vencimiento</th>
                    <th>Notas</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {items.length > 0 ? items.map((item) => (
                    <tr key={item._id}>
                      <td>{item.component}</td>
                      <td>{formatDateOnly(item.lastDone)}</td>
                      <td>{item.nextDueDate ? formatDateOnly(item.nextDueDate) : '—'}</td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--mm-muted)' }}>{item.notes}</td>
                      <td className="text-end" style={{ whiteSpace: 'nowrap' }}>
                        <Button size="sm" variant="outline-secondary" className="mr-1" onClick={() => openEdit(item)}>Editar</Button>
                        <Button size="sm" variant="outline-danger" onClick={() => setItemToDelete(item)}>Eliminar</Button>
                      </td>
                    </tr>
                  )) : (
                    <tr><td colSpan="5" className="text-muted text-center">Sin ítems de mantenimiento registrados.</td></tr>
                  )}
                </tbody>
              </Table>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Modal show={showModal} onHide={() => setShowModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{editingId ? 'Editar' : 'Agregar'} Ítem de Mantenimiento</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-2">
            <Form.Label>Componente</Form.Label>
            <Form.Control
              type="text"
              placeholder='Ej. "Sellos del expansor"'
              value={form.component}
              onChange={(e) => setForm({ ...form, component: e.target.value })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Última Vez Realizado</Form.Label>
            <DateInputDMY
              value={form.lastDone}
              onChange={(iso) => setForm({ ...form, lastDone: iso })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Próximo Vencimiento (opcional)</Form.Label>
            <DateInputDMY
              value={form.nextDueDate}
              onChange={(iso) => setForm({ ...form, nextDueDate: iso })}
            />
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
          <Button variant="primary" onClick={handleSave}>Guardar</Button>
        </Modal.Footer>
      </Modal>

      <ConfirmDeleteModal
        show={!!itemToDelete}
        onHide={() => setItemToDelete(null)}
        onConfirm={handleDelete}
        message={<>¿Estás seguro que deseas eliminar el ítem de mantenimiento <strong>{itemToDelete?.component}</strong>?</>}
      />

      <Modal show={showStopModal} onHide={() => (stopSaving ? null : setShowStopModal(false))} centered>
        <Modal.Header closeButton>
          <Modal.Title>Detener Producción</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {stopError && <div className="alert alert-danger">{stopError}</div>}
          <Form.Group className="mb-3">
            <Form.Label>Motivo</Form.Label>
            <Form.Select value={stopReason} onChange={(e) => setStopReason(e.target.value)}>
              <option value="">Seleccione un motivo</option>
              <option value="Mantenimiento">Mantenimiento</option>
              <option value="Apagon Electrico">Apagón</option>
              <option value="Falta de Cilindros">Falta de Cilindros</option>
              <option value="Otro">Otro</option>
            </Form.Select>
          </Form.Group>
          <Form.Group>
            <Form.Label>Notas</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={stopNotes}
              onChange={(e) => setStopNotes(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowStopModal(false)} disabled={stopSaving}>
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleStopProduction} disabled={stopSaving}>
            {stopSaving ? 'Deteniendo...' : 'Detener'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default PlantHealthPanel;
